import { Instance, SnapshotIn, types } from 'mobx-state-tree'
import { ChecklistKind } from '../Checklists/ChecklistKind'

import { ExistingChecklist, ExistingChecklistModel } from '../Checklists/ChecklistModel'
import { IsEditingModel } from '../IsEditingModel'
import { VehicleProfileModel } from '../ota/VehicleProfileModel'
import type { SelectOption } from 'components/Select/Select'

// Mirrors the server format: registration_number is optional and, when present,
// must be alphanumeric (matching the server-side alphanumeric validation).
const stripNonAlphanumeric = (value: string): string => value.replace(/[^\p{L}\p{N}]/gu, '')

const BaseVehicleModel = types.model('BaseVehicleModel', {
  name: types.maybeNull(types.string),
  // Yes, "number" is actually a string.
  registration_number: types.maybeNull(types.string),
  center_id: types.number,
  checklists: types.array(types.reference(ExistingChecklistModel)),
  // OTA profile of the vehicle. Output only, sent by oversight's vehicle list/get endpoints (missing in GCS).
  vehicle_profile: types.maybeNull(VehicleProfileModel),
})
.views((self) => ({
  get registrationNumber(): typeof self.registration_number {
    return self.registration_number
  },
}))
.views((self) => {
  const getChecklistOfType = (checklistKind: ChecklistKind): ExistingChecklist | null => {
    return self.checklists.find(checklist => checklist.checklistKind === checklistKind) ?? null
  }
  return {
    get launchpadOpenChecklist(): ExistingChecklist | null {
      return getChecklistOfType(ChecklistKind.LaunchpadDayOpen)
    },
    get launchpadCloseChecklist(): ExistingChecklist | null {
      return getChecklistOfType(ChecklistKind.LaunchpadDayClose)
    },
    get preflightChecklist(): ExistingChecklist | null {
      return getChecklistOfType(ChecklistKind.VehiclePreflight)
    },
    get preparationChecklist(): ExistingChecklist | null {
      return getChecklistOfType(ChecklistKind.VehiclePrepare)
    },
    get postflightChecklist(): ExistingChecklist | null {
      return getChecklistOfType(ChecklistKind.VehiclePostflight)
    },
}
})
.actions((self) => {
  const setChecklistOfType = (checklistKind: ChecklistKind, checklist: ExistingChecklist | null) => {
    const currentChecklist = self.checklists.find(checklist => checklist.checklistKind === checklistKind) ?? null
    if (checklist?.id  === currentChecklist?.id) {
      return
    }

    if (currentChecklist !== null) {
      self.checklists.remove(currentChecklist)
    }

    if (checklist !== null) {
      self.checklists.push(checklist)
    }
  }
  return {
    setName(name: typeof self.name): void {
      self.name = name
    },  
    setRegistrationNumber(registrationNumber: typeof self.registration_number) {
      // Only keep alphanumeric characters so the value always fits the server
      // format; an empty result becomes null (the server rejects '').
      const sanitized = registrationNumber != null ? stripNonAlphanumeric(registrationNumber) : null
      self.registration_number = sanitized ? sanitized : null
    },
    setLaunchpadOpenChecklist(checklist: ExistingChecklist | null) {
      setChecklistOfType(ChecklistKind.LaunchpadDayOpen, checklist)
    },
    setLaunchpadCloseChecklist(checklist: ExistingChecklist | null) {
      setChecklistOfType(ChecklistKind.LaunchpadDayClose, checklist)
    },
    setPreflightChecklist(checklist: ExistingChecklist | null) {
      setChecklistOfType(ChecklistKind.VehiclePreflight, checklist)
    },
    setPreparationChecklist(checklist: ExistingChecklist | null) {
      setChecklistOfType(ChecklistKind.VehiclePrepare, checklist)
    },
    setPostflightChecklist(checklist: ExistingChecklist | null) {
      setChecklistOfType(ChecklistKind.VehiclePostflight, checklist)
    },
  }
})


export const ExistingVehicleModel = 
  BaseVehicleModel.named('ExistingVehicleModel')
  .props({
    id: types.identifierNumber,
  })
  .views(self => ({
    get asSelectOption(): SelectOption {
      return { value: self.id.toString(), text: self.name ?? '' }
    }
}))
const NewVehicleModel = 
  BaseVehicleModel.named('NewVehicleModel')
  .props({
    id: types.maybeNull(types.number),
  })
  .actions((self) => ({
    setId(id: number | null) {
      self.id = id
    }
  }))

export const VehicleModel = types.union(ExistingVehicleModel, NewVehicleModel)

export const ExistingVehicleEditingCloneModel = types.compose('ExistingVehicleEditingCloneModel', ExistingVehicleModel, IsEditingModel)
export const NewVehicleEditCloneModel = types.compose('NewVehicleCloneForEditing', NewVehicleModel, IsEditingModel)

export const VehicleEditCloneModel = types.union(
  ExistingVehicleEditingCloneModel, 
  NewVehicleEditCloneModel
)

export type Vehicle = Instance<typeof VehicleModel>
export type ExistingVehicle = Instance<typeof ExistingVehicleModel>
export type ExistingVehicleWithChecklistInstancesSnapshot = 
  Omit<SnapshotIn<typeof ExistingVehicleModel>, 'checklists'> & { checklists: Array<{ id: number }> }
export type VehicleEditClone = Instance<typeof VehicleEditCloneModel>